export const ZOOM_RECORDINGS_GUIDE = {
  id: "zoom-recordings",
  title: "Zoom Recordings & Clips",
  description:
    "How to process Zoom cloud recordings — from download through transcription, AI summary, and automatic clip generation.",
  icon: "Video",
  version: "1.0.0",
  lastUpdated: "2026-03-07",
  requires: ["zoom"],
  sections: [
    /* ---------------------------------------------------------- */
    /*  Overview                                                   */
    /* ---------------------------------------------------------- */
    {
      id: "overview",
      title: "Overview",
      content: [
        {
          type: "paragraph",
          text: "The platform can automatically process your Zoom cloud recordings through a multi-step pipeline:",
        },
        {
          type: "steps",
          items: [
            "**Download** — the recording is streamed from Zoom to Azure Blob Storage",
            "**Transcription** — audio is extracted and sent to AssemblyAI for speaker-labelled transcription",
            "**AI Summary** — the transcript is analysed by GPT-4o to produce an executive summary, key topics, decisions, and action items",
            "**Clip Generation** — AI identifies the most compelling moments and extracts them as short video clips ready for YouTube or social media",
          ],
        },
        {
          type: "paragraph",
          text: "There are two ways recordings enter the pipeline: automatically via Zoom webhooks, or manually via the Zoom Recordings browser.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Automatic Pipeline (Webhooks)                              */
    /* ---------------------------------------------------------- */
    {
      id: "automatic-pipeline",
      title: "Automatic Pipeline (Webhooks)",
      content: [
        {
          type: "paragraph",
          text: "When Zoom cloud recording is enabled on your account, recordings are processed automatically:",
        },
        {
          type: "steps",
          items: [
            "Host a Zoom meeting or webinar with **cloud recording** enabled",
            "When the meeting ends, Zoom processes the recording (this can take a few minutes)",
            "Zoom sends a `recording.completed` webhook to the platform",
            "The platform downloads the recording, extracts audio, and submits it for transcription",
            "Within a few minutes, the transcript, summary, and clips are all generated automatically",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "You don't need to do anything — just host your meeting with cloud recording on, and everything happens in the background.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Manual Processing                                          */
    /* ---------------------------------------------------------- */
    {
      id: "manual-processing",
      title: "Manual Processing (Zoom Recordings Browser)",
      content: [
        {
          type: "paragraph",
          text: "You can also browse and process recordings manually from the Videos page:",
        },
        {
          type: "steps",
          items: [
            "Go to the **Videos** page",
            "Click the **Zoom Recordings** button in the top bar to open the recordings browser",
            "The drawer shows all Zoom cloud recordings from the last 30 days",
            "Click **Process** on any recording to download and transcribe it",
            "The recording enters the pipeline and progresses through each stage automatically",
          ],
        },
        {
          type: "paragraph",
          text: "Use this when you want to process a recording that was made before webhooks were configured, or to re-process a specific recording.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Pipeline Stages                                            */
    /* ---------------------------------------------------------- */
    {
      id: "pipeline-stages",
      title: "Pipeline Stages",
      content: [
        {
          type: "paragraph",
          text: "Each recording progresses through these stages, shown as status badges on the Videos page:",
        },
        {
          type: "table",
          headers: ["Status", "What's happening"],
          rows: [
            ["**Pending Download**", "Waiting for the recording to be downloaded from Zoom"],
            ["**Uploaded**", "Recording is in blob storage, ready for processing"],
            ["**Processing**", "Audio has been submitted to AssemblyAI for transcription"],
            ["**Transcribed**", "Transcript is complete — summary and clips are being generated"],
            ["**Ready**", "Everything is done — transcript, summary, and clips are all available"],
            ["**Error**", "Something went wrong — click the video to see the error message"],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The pipeline runs on a 1-minute timer. Each stage is picked up automatically on the next tick, so a recording typically goes from Processing to Ready within 5–10 minutes depending on length.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Viewing Results                                            */
    /* ---------------------------------------------------------- */
    {
      id: "viewing-results",
      title: "Viewing Results",
      content: [
        {
          type: "heading",
          text: "Opening a Meeting",
        },
        {
          type: "paragraph",
          text: "Click any video on the **Videos** page to open its detail drawer. The drawer shows:",
        },
        {
          type: "steps",
          items: [
            "**Meeting info** — title, date, duration, platform, and status",
            "**Transcript** — the full speaker-labelled transcript with timestamps",
            "**Summary** — AI-generated executive summary, key topics, decisions, and action items",
            "**Clips** — video clips extracted from the recording, playable directly in the browser",
          ],
        },
        {
          type: "heading",
          text: "Playing Clips",
        },
        {
          type: "paragraph",
          text: "Each clip card shows:",
        },
        {
          type: "steps",
          items: [
            "**Title** — a short description of the clip content",
            "**Speaker** — who is speaking in the clip",
            "**Timestamp** — where in the original recording the clip comes from",
            "**Duration** — how long the clip is (typically 20–90 seconds)",
            "A **play button** to watch the clip inline",
          ],
        },
        {
          type: "paragraph",
          text: "Clips are designed to be short, self-contained moments that work well as standalone social media content or highlights.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  What Makes a Good Clip                                     */
    /* ---------------------------------------------------------- */
    {
      id: "clip-selection",
      title: "How Clips Are Selected",
      content: [
        {
          type: "paragraph",
          text: "The AI analyses the full transcript and selects the most compelling moments based on:",
        },
        {
          type: "steps",
          items: [
            "**Key insights or revelations** — surprising data, important conclusions",
            "**Passionate or energetic delivery** — the speaker is clearly engaged",
            "**Actionable advice** — concrete recommendations or strategies",
            "**Compelling Q&A exchanges** — great question with a strong answer",
            "**Memorable quotes** — concise, powerful statements",
            "**Storytelling moments** — anecdotes that illustrate a point",
            "**Debate or constructive disagreement** — different perspectives",
          ],
        },
        {
          type: "paragraph",
          text: "The AI aims for roughly 3 clips per speaker and up to 15 clips total, ensuring each speaker's best contributions are represented. Clips are typically 20–90 seconds long (ideal: ~45 seconds).",
        },
        {
          type: "callout",
          variant: "tip",
          text: "Clips avoid housekeeping, logistics, technical issues, and filler. If a point is repeated, only the best version is selected.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  AI Summary                                                 */
    /* ---------------------------------------------------------- */
    {
      id: "ai-summary",
      title: "AI Summary",
      content: [
        {
          type: "paragraph",
          text: "Every transcribed video gets an AI-generated summary with these sections:",
        },
        {
          type: "table",
          headers: ["Section", "Description"],
          rows: [
            ["**Executive Summary**", "A 2–3 paragraph recap of the video — what was discussed, key outcomes, and overall tone"],
            ["**Key Topics**", "3–8 main subjects covered, each with a short explanation"],
            ["**Decisions**", "What was decided and the context for each decision"],
            ["**Action Items**", "Tasks identified, who they're assigned to, and any deadlines mentioned"],
            ["**Sentiment**", "Overall tone: positive, neutral, mixed, or negative"],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "Speaker labels in the summary match the transcript (e.g. \"Speaker A\", \"Speaker B\"). If you know who was speaking, you can mentally map the labels.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Re-processing a Meeting                                    */
    /* ---------------------------------------------------------- */
    {
      id: "reprocessing",
      title: "Re-processing a Meeting",
      content: [
        {
          type: "paragraph",
          text: "If a video has an error or you want to regenerate the transcript, summary, and clips:",
        },
        {
          type: "steps",
          items: [
            "Open the video from the Videos page",
            "Click the **Reprocess** button",
            "The meeting is re-submitted for transcription and all downstream steps run again",
            "Previous clips and summary are replaced with new ones",
          ],
        },
        {
          type: "callout",
          variant: "warning",
          text: "Re-processing replaces all existing clips and the summary. The original recording is not affected.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Troubleshooting                                            */
    /* ---------------------------------------------------------- */
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: [
        {
          type: "faq",
          items: [
            {
              question: "Meeting stuck in \"Processing\" for a long time",
              answer:
                "Transcription typically takes 2–5 minutes for a 1-hour meeting. If a meeting is stuck in Processing for more than 15 minutes, it will be automatically marked as an error. You can then re-process it.\n\nIf the problem persists, check that the AssemblyAI API key is configured correctly.",
            },
            {
              question: "No clips were generated",
              answer:
                "This can happen if:\n\n- The recording is very short (under a minute)\n- The audio quality is too poor for transcription\n- The transcript has very few phrases\n\nTry re-processing the meeting. If clips still aren't generated, the content may not have enough variation for clip selection.",
            },
            {
              question: "Meeting shows \"Error\" status",
              answer:
                "Click the meeting to see the specific error message in the drawer. Common causes:\n\n- **Transcription job timed out** — the recording may be very long or the service was temporarily unavailable\n- **AssemblyAI API key not configured** — contact your administrator\n- **Blob not found** — the recording file may have been deleted from storage\n\nMost errors can be resolved by clicking **Reprocess**.",
            },
            {
              question: "Zoom Recordings drawer shows \"No recordings found\"",
              answer:
                "The drawer shows cloud recordings from the last 30 days. If you don't see any:\n\n- Make sure cloud recording is enabled on your Zoom account\n- Check that the recording has finished processing on Zoom's side\n- Recordings older than 30 days won't appear (but may already be in the Videos list if previously processed)",
            },
            {
              question: "Recording was processed but has no summary",
              answer:
                "The summary requires Azure OpenAI to be configured. If only the transcript appears without a summary, the OpenAI endpoint may not be set up. Contact your administrator.\n\nNote: clips are only generated after the summary completes, so missing summary also means no clips.",
            },
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Technical Details                                          */
    /* ---------------------------------------------------------- */
    {
      id: "technical-details",
      title: "Technical Details",
      content: [
        {
          type: "paragraph",
          text: "For those interested in how the pipeline works under the hood:",
        },
        {
          type: "table",
          headers: ["Component", "Details"],
          rows: [
            ["**Video storage**", "Azure Blob Storage (`webinar-recordings` container) — full-resolution MP4"],
            ["**Audio extraction**", "Zoom's audio-only file when available; FFmpeg stream copy as fallback (no re-encoding)"],
            ["**Transcription**", "AssemblyAI with Universal-2 model, speaker labels, language detection, auto-chapters, and entity detection"],
            ["**Summary**", "Azure OpenAI GPT-4o with structured JSON output"],
            ["**Clip detection**", "Anthropic Claude for AI-powered highlight selection, with rule-based fallback"],
            ["**Clip extraction**", "FFmpeg stream copy (fast, no re-encoding) with web-optimized MP4 output"],
            ["**Clip storage**", "Azure Blob Storage (`webinar-clips` container)"],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The full-resolution video is always preserved for clip extraction. Audio-only files are used purely for transcription to reduce processing time and cost.",
        },
      ],
    },
  ],
};
